import {
  Box,
  Button,
  Chip,
  TextField,
  Grid,
  Typography,
  Switch,
  Table,
  TableContainer,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  Paper,
  Divider
} from '@mui/material'
import React, { useState, useEffect, Fragment } from 'react'
import {
  PickingDetail,
  PickingGetRouteDetail,
  PickingInputData,
  PickingRoute,
  pickingRouteDetailMovement
} from '../../../interfaces/Picking/Picking'
import { apiCall, ApiConfig } from '../../../config/api'
import { NewPickingRouteHeader } from './NewPickingRouteHeader'
import AddBoxIcon from '@mui/icons-material/AddBox'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
import CustomAlert from '../../_WMS/AlertMessage/AlertMessage'
import { Modal } from '../../_EOS/Modal'
import { eTypePickingRoute } from '../../../enums/eTypePickingRoute'
import { LocalShipping } from '@mui/icons-material'
import { formatExpirationDate } from '../../../utils/dateHelper'
import {
  getExpiredStatusToday,
  ExpiredColorStatus
} from '../../../utils/colors'

interface PickingFinishedProps {
  pickingRoute?: PickingRoute
  handleFinishRoute: (inputData: PickingInputData) => void
  handleLPNButton: () => void
  requiredTote: boolean
}

const detailsInitialState: PickingGetRouteDetail = {
  pickingRouteId: 16,
  userId: 2,
  startDate: '2021-06-30T15:42:14.8623131',
  totalSku: 1,
  realQty: 1,
  pickedQty: 1,
  workOrderId: [5],
  pickingRouteDetailMovement: [
    {
      qtyPicked: 1,
      positionId: 504,
      positionName: 'X.Verde',
      sku: '',
      batch: '',
      ean13: ''
    }
  ],
  packingPositions: [],
  relatedRouteIds: []
}

export const PickingFinished: React.FC<PickingFinishedProps> = ({
  pickingRoute,
  handleFinishRoute,
  handleLPNButton,
  requiredTote
}) => {
  const initialInput: PickingInputData = {
    sku: '',
    quantity: 0,
    positionName: ''
  }
  const [inputData, setInputData] = useState<PickingInputData>(initialInput)
  const [formValues, setValues] = useState(initialInput)
  const [formErrors, setErrors] = useState({} as any)
  const [details, setDetails] =
    useState<PickingGetRouteDetail>(detailsInitialState)
  const [pickingDetail, setPickingDetail] = useState<PickingDetail[]>([
    { position: '', sku: '', qty: 0, batch: '', ean13: '' }
  ])
  const [showHelp, setShowHelp] = useState<boolean>(false)
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false)
  const [showSkuMode, setShowSkuMode] = useState(true)

  const isRecall = pickingRoute?.typePickingRouteId === eTypePickingRoute.Recall

  useEffect(() => {
    apiCall(
      'GET',
      `${ApiConfig.PickingRouteApi}/GetPickingDetail?pickingRouteId=${pickingRoute?.pickingRouteId}`
    ).then(({ data: { data } }) => {
      setDetails(data)
      setPickingDetail(
        data.pickingRouteDetailMovement.map(
          (mov: pickingRouteDetailMovement) => ({
            position: mov.positionName,
            qty: mov.qtyPicked,
            sku: mov.sku,
            batch: mov.batch,
            ean13: mov.ean13,
            expirationDate: mov.expirationDate,
            productName: mov.productName
          })
        )
      )
    })
  }, [])

  const validateInput: any = (name: string, value: string) => {
    const newErrors: any = { ...formErrors }

    if (name === 'positionName') {
      delete newErrors['positionName']
      newErrors.positionName = value ? '' : 'Campo requerido'
    }

    setErrors({
      ...newErrors
    })
  }

  const handleInputValue = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    setValues({
      ...formValues,
      [name]: value
    })
    validateInput(name, value)
    setInputData({ ...inputData, [name]: value })
  }

  const formIsValid = () => {
    const isValid =
      formValues.positionName &&
      Object.values(formErrors).every((x) => x === '')

    return isValid
  }

  const handleFormSubmit = () => {
    const isValid = formIsValid()
    if (isValid) {
      handleFinishRoute(inputData)
    }
  }

  const getExpirationColor = (
    expirationDate: string | null | undefined
  ): string => {
    if (!expirationDate) return 'inherit'
    const status = getExpiredStatusToday(expirationDate, 0)
    if (status === ExpiredColorStatus.expired) return '#dc2626'
    if (status === ExpiredColorStatus.expireSoon) return '#f59e0b'
    return '#16a34a'
  }

  const courierLabel = details.courierName ?? (isRecall ? 'Sin courier' : '—')
  const serviceLabel =
    details.courierServiceName ?? (isRecall ? 'Sin servicio' : '—')

  return (
    <Box
      display='flex'
      flex='1'
      flexDirection='column'
      gap={1.5}
      pb={2}
      width='100%'
      px={1}
    >
      <NewPickingRouteHeader
        pickingRoute={pickingRoute}
        badge={
          <Chip
            icon={
              <CheckCircleIcon style={{ fontSize: 16, color: '#4caf50' }} />
            }
            label='Picking finalizado'
            size='small'
            sx={{
              backgroundColor: '#e8f5e9',
              border: '1px solid #4caf50',
              color: '#5C8E2F',
              fontWeight: 600,
              borderRadius: '20px',
              px: 0.5
            }}
          />
        }
      />

      {details.relatedRouteIds && details.relatedRouteIds.length > 0 && (
        <Box>
          <CustomAlert
            severity='warning'
            message={
              <Fragment>
                Esta ruta está asociada a la{' '}
                <strong>ruta #{details.relatedRouteIds.join(', #')}</strong>.
                <br></br> Deja los productos en el{' '}
                <strong>sector de validación</strong>.
              </Fragment>
            }
          />
        </Box>
      )}

      {/* LPN / TOTE */}
      <Box>
        {(requiredTote || pickingRoute?.lpn) ? (
          <Box
            bgcolor='#EEF4FB'
            border='1px solid #C5D9F0'
            borderRadius={2}
            px={2}
            py={1.5}
          >
            <Typography
              variant='caption'
              display='block'
              color='textSecondary'
              style={{ letterSpacing: 1, fontSize: '0.7rem' }}
            >
              LPN / TOTE
            </Typography>
            <Box display='flex' alignItems='center' gap={1} mt={0.5}>
              <Inventory2Icon fontSize='small' color='primary' />
              <Typography variant='h5' style={{ fontWeight: 700 }}>
                {pickingRoute?.lpn || '—'}
              </Typography>
            </Box>
            {requiredTote && (
              <Button
                fullWidth
                variant='outlined'
                size='small'
                onClick={handleLPNButton}
                sx={{ mt: 1 }}
              >
                {pickingRoute?.lpn ? 'Modificar LPN' : 'Capturar LPN'}
              </Button>
            )}
          </Box>
        ) : (
          <Button
            fullWidth
            variant='contained'
            color='primary'
            endIcon={<AddBoxIcon />}
            onClick={handleLPNButton}
          >
            LPN
          </Button>
        )}
      </Box>

      {/* Ubicación de destino */}
      <Box>
        <Typography variant='caption' color='textSecondary'>
          Ubicación de destino
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mt: 0.5
          }}
        >
          <TextField
            id='positionName'
            name='positionName'
            placeholder='Escanea o ingresa ubicación'
            onChange={handleInputValue}
            onKeyDown={(e) => { if (e.key === 'Enter') handleFormSubmit() }}
            autoFocus
            autoComplete='off'
            size='small'
            fullWidth
            variant='outlined'
          />
          <Button
            onClick={handleFormSubmit}
            variant='contained'
            color='primary'
            disabled={!formIsValid()}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              whiteSpace: 'nowrap',
              flexShrink: 0,
              minWidth: 110
            }}
          >
            Confirmar
          </Button>
        </Box>
      </Box>

      {/* Desconozco la ubicación */}
      <Box>
        <Button
          variant='outlined'
          fullWidth
          onClick={() => setShowHelp(!showHelp)}
        >
          Desconozco la ubicación
        </Button>
        {showHelp && details.packingPositions.length > 0 && (
          <Box mt={1}>
            <CustomAlert
              severity='info'
              title='Cajas disponibles:'
              message={
                <Box
                  component='ul'
                  style={{
                    margin: '4px 0 0',
                    paddingLeft: 0,
                    listStyle: 'none'
                  }}
                >
                  {details.packingPositions.map((pos) => (
                    <Box
                      component='li'
                      key={pos.positionId}
                      display='flex'
                      alignItems='center'
                      gap={0.5}
                      py={0.25}
                    >
                      <UnarchiveIcon style={{ fontSize: 14 }} color='inherit' />
                      <Typography variant='body2'>
                        {pos.positionName}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              }
            />
          </Box>
        )}
      </Box>

      {/* Detalle ruta */}
      <Box>
        <Button
          variant='contained'
          color='primary'
          fullWidth
          onClick={() => setIsDetailModalOpen(true)}
          startIcon={<AddBoxIcon />}
        >
          Detalle ruta
        </Button>
      </Box>

      {/* Resumen inferior */}
      <Box>
        <Divider style={{ marginBottom: 12 }} />
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <Box display='flex' alignItems='center' gap={0.5}>
              <LocalShipping style={{ fontSize: 14, color: '#00000099' }} />
              <Typography
                variant='caption'
                color='textSecondary'
                style={{
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem'
                }}
              >
                Courier
              </Typography>
            </Box>
            <Typography variant='body2' style={{ fontWeight: 600 }}>
              {courierLabel}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box display='flex' alignItems='center' gap={0.5}>
              <Typography
                variant='caption'
                color='textSecondary'
                style={{
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem'
                }}
              >
                Servicio
              </Typography>
            </Box>
            <Box>
              <Typography
                component='span'
                variant='body2'
                style={{
                  fontWeight: 600,
                  background: details.courierServiceName
                    ? '#E8F0FE'
                    : undefined,
                  borderRadius: 4
                }}
              >
                {serviceLabel}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant='caption'
              color='textSecondary'
              style={{
                letterSpacing: 1,
                textTransform: 'uppercase',
                fontSize: '0.65rem'
              }}
            >
              SKU
            </Typography>
            <Typography variant='body2' style={{ fontWeight: 600 }}>
              {details.totalSku}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant='caption'
              color='textSecondary'
              style={{
                letterSpacing: 1,
                textTransform: 'uppercase',
                fontSize: '0.65rem'
              }}
            >
              Cantidad
            </Typography>
            <Typography variant='body2' style={{ fontWeight: 600 }}>
              {details.pickedQty} / {details.realQty}
            </Typography>
          </Grid>
          {details.orderReferenceId && (
            <Grid item xs={6}>
              <Typography
                variant='caption'
                color='textSecondary'
                style={{
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  fontSize: '0.65rem'
                }}
              >
                Pedido
              </Typography>
              <Typography variant='body2' style={{ fontWeight: 600 }}>
                {details.orderReferenceId}
              </Typography>
            </Grid>
          )}
          <Grid item xs={6}>
            <Typography
              variant='caption'
              color='textSecondary'
              style={{
                letterSpacing: 1,
                textTransform: 'uppercase',
                fontSize: '0.65rem'
              }}
            >
              OT
            </Typography>
            <Typography variant='body2' style={{ fontWeight: 600 }}>
              {details.workOrderId.join('-')}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Modal Detalle ruta */}
      <Modal
        open={isDetailModalOpen}
        title='Detalle ruta'
        onClose={() => setIsDetailModalOpen(false)}
      >
        <Box display='flex' alignItems='center' mb={1} gap={1}>
          <Typography variant='body2'>EAN</Typography>
          <Switch
            checked={showSkuMode}
            onChange={() => setShowSkuMode(!showSkuMode)}
            size='small'
          />
          <Typography variant='body2'>SKU</Typography>
        </Box>
        <TableContainer component={Paper} variant='outlined'>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Producto</TableCell>
                <TableCell align='center'>Cant</TableCell>
                <TableCell align='center'>Lote</TableCell>
                <TableCell align='center'>Vencimiento</TableCell>
                <TableCell align='center'>Posición</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pickingDetail.map((item, i) => (
                <TableRow key={i}>
                  <TableCell>
                    {item.productName ||
                      (showSkuMode ? item.sku : item.ean13) ||
                      '—'}
                  </TableCell>
                  <TableCell align='center'>{item.qty}</TableCell>
                  <TableCell align='center'>
                    <Typography
                      variant='body2'
                      style={{
                        fontWeight: 600,
                        color: item.expirationDate
                          ? getExpirationColor(item.expirationDate)
                          : 'inherit'
                      }}
                    >
                      {item.batch || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align='center'>
                    {item.expirationDate ? (
                      <Box
                        display='flex'
                        alignItems='center'
                        justifyContent='center'
                        gap={0.5}
                      >
                        <Typography
                          variant='body2'
                          style={{
                            color: getExpirationColor(item.expirationDate),
                            fontWeight: 600
                          }}
                        >
                          {formatExpirationDate(item.expirationDate)}
                        </Typography>
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align='center'>{item.position}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box display='flex' justifyContent='space-between' mt={2} px={1}>
          <Typography variant='subtitle2' style={{ fontWeight: 700 }}>
            TOTAL UNIDADES
          </Typography>
          <Typography variant='subtitle2' style={{ fontWeight: 700 }}>
            {pickingDetail.reduce((acc, item) => acc + item.qty, 0)}
          </Typography>
        </Box>
      </Modal>
    </Box>
  )
}
